import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getGtmAttr,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import type { SeedingProposalStatus, SeedingRecruitmentStatus } from '@musinsa/services-curator';

/**
 * 큐레이터 상품 시딩 — 무신사(Amplitude + GTM/GA4) 이벤트 analytics.
 *
 * 시스템 매핑은 정의서 소스가 상보적이라 화면별로 다르다:
 * - 시딩 리스트/상세: Amplitude 정의서(amp-seeding-list.md, amp-seeding-detail.md) 추가 제공.
 *   GTM(GA4) + Amplitude 이중 발화.
 * - 29CM Snowplow 는 전 화면 커버 → 각 컴포넌트에서 `track()` 인라인 (SalesRankingSection 패턴).
 *
 * leaf 컴포넌트에서 `if (isMusinsaService(service))` 가드 후 호출.
 * mss 페어리티 베이스 없음 — 정의서 spec 그대로 발화(키·section_index 리네이밍 금지).
 */

// Amplitude screen name 상수
const SCREEN_SEEDING_LIST = 'screen_curator_seeding_campaign_list';
const SCREEN_SEEDING_DETAIL = 'screen_curator_seeding_campaign_detail';

// GA4 section_name (정의서 "노출영역명" 컬럼)
const GA4_SECTION_CATEGORY_FILTER = 'category_filter';
const GA4_SECTION_CAMPAIGN_LIST = 'campaign_list';
const GA4_SECTION_APPLY = 'apply';

// GA4 section_index (정의서 "노출영역 순서" 컬럼)
const SECTION_INDEX_CATEGORY_FILTER = '1';
const SECTION_INDEX_CAMPAIGN_LIST = '4';

/**
 * ① 시딩 캠페인 리스트 > 카테고리 필터 클릭 (GA4 click_button).
 * 정의서: section_name=category_filter / section_index=1 / button_name={filter_name}.
 * brand_id 는 카테고리 필터 전역 GA4 슬롯에만 적용 — curatorId 는 GA4 미지원, 29CM 전용.
 */
export function trackSeedingListCategoryFilterClickMusinsa(params: { filterName: string }): void {
  const payload = {
    brand_id: GTM_NOTSET_STRING,
    button_id: GA4_SECTION_CATEGORY_FILTER,
    button_name: params.filterName,
    index: GTM_NOTSET_STRING,
    section_index: SECTION_INDEX_CATEGORY_FILTER,
    section_name: GA4_SECTION_CATEGORY_FILTER,
  };
  pushDataLayer('click_button', payload);
}

/**
 * ① 시딩 캠페인 리스트 > 캠페인 노출 (GA4 impression_content).
 * `impression_content` 는 pushDataLayer 미지원 → getGtmAttr 으로 노출영역 element 의
 * className + data-* attribute 로 인코딩, GTM 이 viewport 진입 시 자동 발화.
 * 콜사이트에서 `{ className, ...rest }` 분리해 className 은 className, 나머지는 spread.
 */
export function buildSeedingCampaignImpressionGtmAttr(params: {
  campaignId: number;
  /** 리스트 내 0-based map index */
  index: number;
  /** 리스트 item.brandId (RecruitmentCampaignItem.brandId: string) */
  brandId: string;
  /** curatorId = userInfo.snsTitle. 빈값이면 GTM_NOTSET_STRING */
  curatorId: string;
}) {
  return getGtmAttr(['impression_content'], {
    brand_id: params.brandId || GTM_NOTSET_STRING,
    content_id: String(params.campaignId),
    content_profile_id: params.curatorId || GTM_NOTSET_STRING,
    index: String(params.index + 1),
    section_index: SECTION_INDEX_CAMPAIGN_LIST,
    section_name: GA4_SECTION_CAMPAIGN_LIST,
  });
}

/**
 * ① 시딩 캠페인 리스트 > 캠페인 클릭 (GA4 click_content).
 */
export function trackSeedingCampaignClickMusinsa(params: {
  campaignId: number;
  /** 리스트 내 0-based map index */
  index: number;
  /** 리스트 item.brandId */
  brandId: string;
  /** curatorId = userInfo.snsTitle. 빈값이면 GTM_NOTSET_STRING */
  curatorId: string;
}): void {
  const payload = {
    brand_id: params.brandId || GTM_NOTSET_STRING,
    content_id: String(params.campaignId),
    // content_name 은 GA4 모집형 리스트 정의서 no.3 에 미정의이나 ClickContentEvent 타입 필수 →
    // 의미없는 campaignId 대신 (not set) 적재(parity).
    content_name: GTM_NOTSET_STRING,
    content_profile_id: params.curatorId || GTM_NOTSET_STRING,
    index: String(params.index + 1),
    section_index: SECTION_INDEX_CAMPAIGN_LIST,
    section_name: GA4_SECTION_CAMPAIGN_LIST,
  };
  pushDataLayer('click_content', payload);
}

/**
 * ② 시딩 캠페인 상세 > 신청하기 클릭 (GA4 click_button, 신청 완료 시점 적재).
 * 정의서(ga4-seeding-detail no.1): section_name=apply / button_name=신청하기 / campaign_id 는 `extra_info` 컬럼.
 * (click_button 에 content_id 슬롯은 없으나 extra_info 슬롯은 있어 campaign_id 적재 가능.)
 */
export function trackSeedingApplyClickMusinsa(params: {
  campaignId: number;
  /** 버튼명 — 모집형 '신청하기' / 제안형 '수락하기' (동일 no.2 이벤트, 버튼명으로 구분) */
  buttonName: string;
}): void {
  const payload = {
    brand_id: GTM_NOTSET_STRING,
    button_id: 'curator_seeding_apply',
    button_name: params.buttonName,
    // GA4 정의서: 신청하기 campaign_id 는 extra_info 컬럼에 적재
    extra_info: String(params.campaignId),
    index: GTM_NOTSET_STRING,
    section_index: GTM_NOTSET_STRING,
    section_name: GA4_SECTION_APPLY,
  };
  pushDataLayer('click_button', payload);
}

// ─────────────────────────────────────────────────────────────
// Amplitude (AMP 로그정의서 — amp-seeding-list.md / amp-seeding-detail.md)
// ─────────────────────────────────────────────────────────────

/**
 * 시딩 캠페인 리스트 > 카테고리 필터 클릭 — Amplitude (no.1).
 * section_name=category_filter / name={filter_name}
 */
export function trackSeedingListCategoryFilterClickAmplitude(params: { filterName: string }): void {
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_SEEDING_LIST,
    section_name: 'category_filter',
    name: params.filterName,
  });
}

/**
 * 시딩 캠페인 리스트 > 토글 필터(좋아요한 브랜드) 클릭 — Amplitude (no.3).
 * section_name=toggle_filter / name=liked_brand / toggle=on|off
 */
export function trackSeedingListToggleFilterClickAmplitude(params: { toggle: 'on' | 'off' }): void {
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_SEEDING_LIST,
    section_name: 'toggle_filter',
    name: 'liked_brand',
    toggle: params.toggle,
  });
}

/**
 * 시딩 캠페인 리스트 > 정렬 필터 클릭 — Amplitude (no.4).
 * section_name=sort_filter / name={filter_name}(라벨)
 */
export function trackSeedingListSortFilterClickAmplitude(params: { filterName: string }): void {
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_SEEDING_LIST,
    section_name: 'sort_filter',
    name: params.filterName,
  });
}

/**
 * 시딩 캠페인 리스트 > 캠페인 클릭 — Amplitude (no.5).
 * section_name=campaign_list / id={curator_id} / all_property: campaign_id, brand_id
 */
export function trackSeedingCampaignClickAmplitude(params: {
  campaignId: number;
  /** 리스트 item.brandId */
  brandId: string;
  /** curatorId = userInfo.snsTitle */
  curatorId: string;
}): void {
  const campaign_id = String(params.campaignId);
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_SEEDING_LIST,
    section_name: 'campaign_list',
    id: params.curatorId ?? '',
    campaign_id,
    brand_id: params.brandId ?? '',
  });
}

/**
 * 시딩 캠페인 상세 > 신청하기 클릭 — Amplitude (no.2, 신청 완료 시점 적재).
 * section_name=apply / id={curator_id} / button_name=신청하기
 */
export function trackSeedingApplyClickAmplitude(params: {
  campaignId: number;
  /** brand_id — SeedingCampaignDetail.brand.brandId (optional이므로 없으면 ''). */
  brandId?: string;
  /** curatorId = userInfo.snsTitle */
  curatorId?: string;
  /** 버튼명 — 모집형 '신청하기' / 제안형 '수락하기' */
  buttonName: string;
}): void {
  const campaign_id = String(params.campaignId);
  const brand_id = params.brandId ?? '';
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_SEEDING_DETAIL,
    section_name: 'apply',
    id: params.curatorId ?? '',
    button_name: params.buttonName,
    campaign_id,
    brand_id,
  });
}

// ─────────────────────────────────────────────────────────────
// 협업하기(시딩) 캠페인 카드 — 무신사 GA4 + Amplitude
// AMP 정의서: amp-collaboration.md no.12/13/14
// GA4 정의서: ga4-collaboration-list.md no.1/2
// screen: screen_curator_collaboration / page_id: /curator/collaborate_brand_campaign
// ─────────────────────────────────────────────────────────────

const SCREEN_COLLABORATION = 'screen_curator_collaboration';
const GA4_SECTION_COLLABORATE_LIST = 'campaign_list';

/**
 * SeedingProposalStatus(PENDING/CONFIRMED/COMPLETED/REJECTED) →
 * Amplitude/GA4 applied_tab 값(suggestion/acception/completion/rejection).
 * AMP 정의서 각주 [9]·[10]: tab 이름 기준.
 */
function toCollaborateProposalAppliedTab(searchStatus: SeedingProposalStatus): string {
  switch (searchStatus) {
    case 'PENDING':
      return 'suggestion';
    case 'CONFIRMED':
      return 'acception';
    case 'COMPLETED':
      return 'completion';
    case 'REJECTED':
      return 'rejection';
  }
}

/**
 * SeedingRecruitmentStatus(PENDING/CONFIRMED/COMPLETED/NOT_SELECTED) →
 * Amplitude/GA4 applied_tab 값(suggestion/acception/completion/rejection).
 * 내가 신청한 카드는 NOT_SELECTED=거절(rejection) 탭에 대응.
 */
function toCollaborateRecruitmentAppliedTab(searchStatus: SeedingRecruitmentStatus): string {
  switch (searchStatus) {
    case 'PENDING':
      return 'suggestion';
    case 'CONFIRMED':
      return 'acception';
    case 'COMPLETED':
      return 'completion';
    case 'NOT_SELECTED':
      return 'rejection';
  }
}

/**
 * 협업하기(시딩) > 제안 받은 카드(SeedingProposalCard) 노출 — GA4 impression_content.
 * 정의서: ga4-collaboration-list.md no.1.
 * 카드 루트 element 에 `{ className, ...rest }` spread.
 * NOTE: brand_id 는 me/proposal 응답의 brandId(BE 추가 예정, nullish) 사용 — 미수신 시 NOTSET.
 */
export function buildCollaborateProposalImpressionGtmAttr(params: {
  campaignId: number;
  searchStatus: SeedingProposalStatus;
  /** me/proposal item.brandId (nullish) — 없으면 NOTSET */
  brandId?: string | null;
  /** curatorId = userInfo.snsTitle. 빈값이면 GTM_NOTSET_STRING */
  curatorId: string;
}) {
  return getGtmAttr(['impression_content'], {
    applied_tab: toCollaborateProposalAppliedTab(params.searchStatus),
    content_id: String(params.campaignId),
    content_type: 'seeding',
    brand_id: params.brandId || GTM_NOTSET_STRING,
    content_profile_type: params.curatorId || GTM_NOTSET_STRING,
    // 정의서 no.1 section_index·index 공란 → GTM_NOTSET_STRING
    index: GTM_NOTSET_STRING,
    section_index: GTM_NOTSET_STRING,
    section_name: GA4_SECTION_COLLABORATE_LIST,
  });
}

/**
 * 협업하기(시딩) > 내가 신청한 카드(SeedingRecruitmentCard) 노출 — GA4 impression_content.
 * 정의서: ga4-collaboration-list.md no.1.
 * NOTE: brand_id 는 me/recruitment 응답의 brandId(OpenAPI 미선언, nullish) 사용.
 */
export function buildCollaborateRecruitmentImpressionGtmAttr(params: {
  campaignId: number;
  searchStatus: SeedingRecruitmentStatus;
  /** me/recruitment item.brandId (nullish) — 없으면 NOTSET */
  brandId?: string | null;
  /** curatorId = userInfo.snsTitle. 빈값이면 GTM_NOTSET_STRING */
  curatorId: string;
}) {
  return getGtmAttr(['impression_content'], {
    applied_tab: toCollaborateRecruitmentAppliedTab(params.searchStatus),
    content_id: String(params.campaignId),
    content_type: 'seeding',
    brand_id: params.brandId || GTM_NOTSET_STRING,
    content_profile_type: params.curatorId || GTM_NOTSET_STRING,
    // 정의서 no.1 section_index·index 공란 → GTM_NOTSET_STRING
    index: GTM_NOTSET_STRING,
    section_index: GTM_NOTSET_STRING,
    section_name: GA4_SECTION_COLLABORATE_LIST,
  });
}

/**
 * 협업하기(시딩) > 제안 받은 카드 클릭 — GA4 click_content + Amplitude no.12.
 * GA4: ga4-collaboration-list.md no.2.
 * AMP: amp-collaboration.md no.12 / name=brandsuggestion_seeding_campaign.
 * NOTE: brand_id 는 me/proposal 응답의 brandId(BE 추가 예정, nullish) 사용 — 미수신 시 NOTSET.
 * campaign_title(button_name): me/proposal title(BE 추가 예정) → 미수신 시 호출부에서 goodsName 폴백.
 */
export function trackCollaborateProposalCardClickMusinsa(params: {
  campaignId: number;
  searchStatus: SeedingProposalStatus;
  /** campaign_title — me/proposal title ?? goodsName(폴백) */
  campaignTitle: string;
  /** me/proposal item.brandId (nullish) — 없으면 NOTSET */
  brandId?: string | null;
  /** curatorId = userInfo.snsTitle. 빈값이면 GTM_NOTSET_STRING */
  curatorId: string;
}): void {
  const campaign_id = String(params.campaignId);
  const applied_tab = toCollaborateProposalAppliedTab(params.searchStatus);

  pushDataLayer('click_content', {
    applied_tab,
    content_id: campaign_id,
    content_name: params.campaignTitle,
    content_type: 'seeding',
    brand_id: params.brandId || GTM_NOTSET_STRING,
    content_profile_type: params.curatorId || GTM_NOTSET_STRING,
    // 정의서 no.2 section_index·index 공란 → GTM_NOTSET_STRING
    index: GTM_NOTSET_STRING,
    section_index: GTM_NOTSET_STRING,
    section_name: GA4_SECTION_COLLABORATE_LIST,
  });

  // AMP no.12: 브랜드 제안 시딩 캠페인 클릭 (collab AMP id=campaign_id 유지)
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    section_name: GA4_SECTION_COLLABORATE_LIST,
    name: 'brandsuggestion_seeding_campaign',
    id: campaign_id,
    // 🚩 campaign_title DTO 미제공 — goodsName 잠정 사용
    button_name: params.campaignTitle,
    applied_tab,
  });
}

/**
 * 협업하기(시딩) > 내가 신청한 카드 클릭 — GA4 click_content + Amplitude no.13.
 * GA4: ga4-collaboration-list.md no.2.
 * AMP: amp-collaboration.md no.13 / name=curatorsuggestion_seeding_campaign.
 * NOTE: brand_id 는 me/recruitment 응답의 brandId(OpenAPI 미선언, nullish) 사용.
 * 🚩 campaign_title(button_name): DTO에 campaign_title 없음 → goodsName 잠정 사용 + 보고.
 */
export function trackCollaborateRecruitmentCardClickMusinsa(params: {
  campaignId: number;
  searchStatus: SeedingRecruitmentStatus;
  /** 🚩 campaign_title DTO 미제공 — goodsName 잠정 사용 */
  campaignTitle: string;
  /** me/recruitment item.brandId (nullish) — 없으면 NOTSET */
  brandId?: string | null;
  /** curatorId = userInfo.snsTitle. 빈값이면 GTM_NOTSET_STRING */
  curatorId: string;
}): void {
  const campaign_id = String(params.campaignId);
  const applied_tab = toCollaborateRecruitmentAppliedTab(params.searchStatus);

  pushDataLayer('click_content', {
    applied_tab,
    content_id: campaign_id,
    // 🚩 campaign_title DTO 미제공 — goodsName 잠정 사용
    content_name: params.campaignTitle,
    content_type: 'seeding',
    brand_id: params.brandId || GTM_NOTSET_STRING,
    content_profile_type: params.curatorId || GTM_NOTSET_STRING,
    // 정의서 no.2 section_index·index 공란 → GTM_NOTSET_STRING
    index: GTM_NOTSET_STRING,
    section_index: GTM_NOTSET_STRING,
    section_name: GA4_SECTION_COLLABORATE_LIST,
  });

  // AMP no.13: 내가 제안한 시딩 캠페인 클릭 (collab AMP id=campaign_id 유지)
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    section_name: GA4_SECTION_COLLABORATE_LIST,
    name: 'curatorsuggestion_seeding_campaign',
    id: campaign_id,
    // 🚩 campaign_title DTO 미제공 — goodsName 잠정 사용
    button_name: params.campaignTitle,
    applied_tab,
  });
}

/**
 * 협업하기(시딩) > 제안 받은 카드 수락/거절 버튼 클릭 — Amplitude no.14.
 * AMP: amp-collaboration.md no.14 / button_name=수락/거절.
 * SeedingProposalCard 내 showActions(=PENDING 탭) 분기에서만 노출되는 버튼.
 * 🚩 campaign_title(button_name): DTO에 campaign_title 없음 → goodsName 잠정 사용 + 보고.
 * NOTE: collab AMP no.14 에 curator_id 슬롯 없음 — id=campaign_id 유지(정의서).
 */
export function trackCollaborateProposalActionClickMusinsa(params: {
  campaignId: number;
  searchStatus: SeedingProposalStatus;
  /** 수락 버튼='수락', 거절 버튼='거절' — 정의서 button_name=수락/거절 (no.14) */
  action: '수락' | '거절';
  /** 🚩 campaign_title DTO 미제공 — goodsName 잠정 사용 */
  campaignTitle: string;
  /** curatorId — 현재 no.14 AMP 정의서에 curator_id 슬롯 없음. 시그니처만 통일. */
  curatorId?: string;
}): void {
  const campaign_id = String(params.campaignId);
  const applied_tab = toCollaborateProposalAppliedTab(params.searchStatus);

  // AMP no.14: 브랜드 제안 시딩 캠페인 수락/거절 클릭 (id=campaign_id 유지 — 정의서)
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    section_name: GA4_SECTION_COLLABORATE_LIST,
    // 🚩 campaign_title DTO 미제공 — goodsName 잠정 사용
    name: params.campaignTitle,
    id: campaign_id,
    button_name: params.action,
    applied_tab,
  });
}
